// Shared admin UI components (badges, cards, row, password modal).
// Extracted from AdminDashboard.jsx to keep that file lean.
import React, { useState } from 'react';
import axios from 'axios';
import {
  Flame, AlertTriangle, MessageSquare, Eye, EyeOff, KeyRound, X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import {
  API, getAuthHeaders, isHotLead, timeAgo, getLastActivity, isStale, hasUnreadSms,
  STATUS_CONFIG,
} from '../../utils/admin-helpers';

export function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  return (
    <span
      className={`px-2 py-0.5 rounded-md text-[11px] font-medium whitespace-nowrap ${cfg.color}`}
      data-testid={`status-badge-${status}`}
    >
      {cfg.label}
    </span>
  );
}

export function StatCard({ icon: Icon, label, value, color, subtext }) {
  const colorMap = {
    cyan: 'bg-cyan-500/10 text-cyan-400',
    violet: 'bg-violet-500/10 text-violet-400',
    emerald: 'bg-emerald-500/10 text-emerald-400',
    amber: 'bg-amber-500/10 text-amber-400',
    blue: 'bg-blue-500/10 text-blue-400',
    rose: 'bg-rose-500/10 text-rose-400',
  };

  return (
    <div
      className="p-5 rounded-xl bg-slate-900 border border-slate-800 hover:border-slate-700 transition-colors"
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${colorMap[color]} flex items-center justify-center`}>
          <Icon className="h-5 w-5" />
        </div>
        <span className="text-slate-400 text-sm font-medium">{label}</span>
      </div>
      <div className="text-2xl sm:text-3xl font-bold text-white">{value}</div>
      {subtext && <p className="text-slate-500 text-xs mt-1">{subtext}</p>}
    </div>
  );
}

export function HotBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-500/15 text-orange-400 text-[10px] font-bold uppercase tracking-wider animate-pulse"
      data-testid="hot-badge"
    >
      <Flame className="h-3 w-3" /> HOT
    </span>
  );
}

export function ScoreBadge({ score }) {
  if (!score) return null;
  const { score: val, label } = score;
  const cfg = label === 'hot'
    ? { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', glow: 'ring-1 ring-red-500/20' }
    : label === 'warm'
    ? { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30', glow: '' }
    : { bg: 'bg-slate-500/15', text: 'text-slate-400', border: 'border-slate-500/30', glow: '' };
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ${cfg.bg} ${cfg.text} ${cfg.border} ${cfg.glow} border text-[10px] font-bold uppercase tracking-wider`}
      data-testid="score-badge"
    >
      {val}
    </span>
  );
}

export function LeadRow({ lead, onToggleRead, onSelect }) {
  const company = lead.company || '';
  const parts = company.split(' | ');
  const org = parts[0] || '';
  const hot = isHotLead(lead.created_at);
  const stale = isStale(lead);
  const smsReply = hasUnreadSms(lead);
  const nextTask = (lead.tasks || [])
    .filter((t) => t.status === 'pending')
    .sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''))[0];
  const todayStr = new Date().toISOString().slice(0, 10);
  const taskOverdue = nextTask?.due_date && nextTask.due_date.slice(0, 10) < todayStr;
  const lastActivity = getLastActivity(lead);
  const es = lead.engagement_score;

  return (
    <tr
      className={`border-b border-slate-800 hover:bg-slate-800/50 transition-colors cursor-pointer ${
        !lead.read ? 'bg-cyan-500/[0.03]' : ''
      } ${stale ? 'border-l-2 border-l-red-500' : hot && !lead.read ? 'border-l-2 border-l-orange-500' : ''}`}
      data-testid={`lead-row-${lead.id}`}
      onClick={() => onSelect(lead.id)}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 flex-wrap">
          {!lead.read && <span className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0" />}
          <span className="text-white font-medium text-sm">{lead.name}</span>
          {hot && <HotBadge />}
          {stale && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-red-500/15 text-red-400 text-[10px] font-bold uppercase tracking-wider"
              data-testid={`stale-badge-${lead.id}`}
            >
              <AlertTriangle className="h-3 w-3" /> STALE
            </span>
          )}
          {smsReply && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 text-[10px] font-bold"
              data-testid={`sms-reply-badge-${lead.id}`}
            >
              <MessageSquare className="h-3 w-3" /> SMS
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-slate-300 text-sm hidden sm:table-cell">{org}</td>
      <td className="px-4 py-3 text-sm hidden md:table-cell">
        <StatusBadge status={lead.status || 'new'} />
      </td>
      <td className="px-4 py-3 text-sm hidden md:table-cell">
        {es ? <ScoreBadge score={es} /> : <span className="text-slate-600">—</span>}
      </td>
      <td className="px-4 py-3 text-sm hidden lg:table-cell">
        {lead.proposal_value ? (
          <span className="text-emerald-400 font-medium">${lead.proposal_value.toLocaleString()}</span>
        ) : (
          <span className="text-slate-600">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm hidden lg:table-cell">
        <span className={lead.assigned_to ? 'text-slate-300' : 'text-slate-600'}>
          {lead.assigned_to || '—'}
        </span>
      </td>
      <td className="px-4 py-3 text-xs hidden xl:table-cell">
        {nextTask ? (
          <span className={`flex items-center gap-1 ${taskOverdue ? 'text-red-400' : 'text-slate-400'}`}>
            {taskOverdue && <AlertTriangle className="h-3 w-3" />}
            {nextTask.title.length > 20 ? nextTask.title.slice(0, 20) + '...' : nextTask.title}
          </span>
        ) : (
          <span className="text-slate-600">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-xs hidden md:table-cell whitespace-nowrap">
        <div className="flex flex-col">
          {hot ? (
            <span className="text-orange-400 font-medium">{timeAgo(lead.created_at)}</span>
          ) : (
            <span className="text-slate-500">
              {new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
          {lastActivity !== lead.created_at && (
            <span className="text-slate-600 text-[10px] mt-0.5" data-testid={`last-activity-${lead.id}`}>
              Active {timeAgo(lastActivity)}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => onToggleRead(lead.id, lead.read)}
          className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors"
          data-testid={`toggle-read-${lead.id}`}
        >
          {lead.read ? (
            <EyeOff className="h-4 w-4 text-slate-500" />
          ) : (
            <Eye className="h-4 w-4 text-cyan-400" />
          )}
        </button>
      </td>
    </tr>
  );
}

export function ChangePasswordModal({ onClose }) {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/admin/change-password`,
        { current_password: currentPw, new_password: newPw },
        { headers: getAuthHeaders() },
      );
      toast.success('Password updated successfully');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div
        className="bg-slate-900 rounded-2xl border border-slate-800 p-6 sm:p-8 w-full max-w-md"
        data-testid="change-password-modal"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-white font-bold text-lg flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-cyan-400" /> Change Password
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-slate-400 text-sm mb-2 block">Current Password</label>
            <Input
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              required
              data-testid="current-password-input"
              className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
            />
          </div>
          <div>
            <label className="text-slate-400 text-sm mb-2 block">New Password</label>
            <Input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              required
              placeholder="Min 8 characters"
              data-testid="new-password-input"
              className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="save-password-button"
            className="w-full bg-white text-slate-950 hover:bg-slate-100 font-semibold py-5"
          >
            {loading ? 'Updating...' : 'Update Password'}
          </Button>
        </form>
      </div>
    </div>
  );
}
